import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { billingKeys, BILLING_STALE_TIME_MS } from '../../services/queryClient';
import {
  Button,
  Card,
  CardContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { RefreshCw } from 'lucide-react';
import EOENotReadyTab from '../../components/billing/tabs/EOENotReadyTab';
import EOEPaidTab from '../../components/billing/tabs/EOEPaidTab';
import EOEPendingApprovalTab from '../../components/billing/tabs/EOEPendingApprovalTab';
import EOEPendingPaymentTab from '../../components/billing/tabs/EOEPendingPaymentTab';
import EOEReadyTab from '../../components/billing/tabs/EOEReadyTab';
import EOEReadyToSendTab from '../../components/billing/tabs/EOEReadyToSendTab';
import EOERejectedCancelledTab from '../../components/billing/tabs/EOERejectedCancelledTab';
import RemittanceManager, {
  type RemittanceManagerHandle,
} from '../../components/billing/RemittanceManager';
import { TabCountPill } from '../../components/billing/TabCountPill';
import {
  EOEClaimsManagerTabProvider,
  type BillingOptionItem,
} from '../../components/billing/EOEClaimsManagerContext';
import {
  EOE_CLAIM_TABS,
  type EoeClaimTabId,
} from '../../components/billing/eoeClaimsManagerTabs';
import {
  getBillingCyclesMeta,
  getRefreshBillingCyclesStatus,
  refreshBillingCycles,
} from '../../services/api';
import type {
  BillingCycleTabCounts,
  EoeClaimTabMetadata,
} from '../../services/api/types.gen';
import { useStore } from '../../store';

const EMPTY_TAB_COUNTS: Required<BillingCycleTabCounts> = {
  not_ready: 0,
  ready: 0,
  pending_approval: 0,
  ready_to_send: 0,
  pending_payment: 0,
  paid: 0,
  rejected_cancelled: 0,
};

interface EOEStatusTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const TAB_COMPONENTS: Record<
  EoeClaimTabId,
  React.ComponentType<EOEStatusTabProps>
> = {
  'not-ready': EOENotReadyTab,
  ready: EOEReadyTab,
  'pending-approval': EOEPendingApprovalTab,
  'ready-to-send': EOEReadyToSendTab,
  'pending-payment': EOEPendingPaymentTab,
  paid: EOEPaidTab,
  'rejected-cancelled': EOERejectedCancelledTab,
};

const getCountText = (
  tabCounts: BillingCycleTabCounts | null,
  countKey: string
) => {
  if (!tabCounts) return '-';
  const count = tabCounts[countKey as keyof BillingCycleTabCounts];
  return typeof count === 'number' ? count.toString() : '-';
};

const VIEW_HEADINGS: Record<
  'claims' | 'remittances',
  { title: string; description: string }
> = {
  claims: {
    title: 'Episodic Claims',
    description:
      'Manage and track episodic claims through the billing lifecycle',
  },
  remittances: {
    title: 'Remittances',
    description: '835 remittance advices received or uploaded for this agency',
  },
};

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 60;
const TERMINAL_FAILURES = ['FAILED', 'TERMINATED', 'CANCELED', 'TIMED_OUT'];

const EOEClaimsManager: React.FC = () => {
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const defaultTabId = EOE_CLAIM_TABS[0].id;
  const [activeView, setActiveView] = useState<'claims' | 'remittances'>(
    'claims'
  );
  const [activeTab, setActiveTab] = useState<EoeClaimTabId>(defaultTabId);
  const [mountedTabIds, setMountedTabIds] = useState<Set<EoeClaimTabId>>(
    () => new Set([defaultTabId])
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const remittanceRef = useRef<RemittanceManagerHandle>(null);
  const isMountedRef = useRef(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Supplies the tab list, the tab counts, and the agency-wide insurance/clinic
  // option lists for the toolbar dropdowns. The request-id guard this replaced
  // is now structural — one cache entry, so there is nothing to race.
  const metaQuery = useQuery({
    queryKey: billingKeys.billingMeta(),
    queryFn: async () => {
      const response = await getBillingCyclesMeta({});
      if (response.error) {
        throw response.error;
      }
      return response.data;
    },
    staleTime: BILLING_STALE_TIME_MS,
    // Toasted once per failed fetch by the shared handler in queryClient.ts,
    // rather than from an effect that would re-fire on every remount.
    meta: { errorToast: { title: 'Failed to load billing cycle metadata' } },
  });

  // Every derived value below is gated on this rather than on `metaQuery.data`
  // alone: a failed *refetch* leaves the last successful payload in the cache,
  // so reading `data` directly would show the counts blank while the dropdowns
  // still offered options from a load that has since failed.
  const meta = metaQuery.isError ? undefined : metaQuery.data;

  // Backend-provided tabs when available, otherwise the hardcoded fallback.
  const tabs = useMemo<EoeClaimTabMetadata[]>(
    () => meta?.tabs ?? ([...EOE_CLAIM_TABS] as EoeClaimTabMetadata[]),
    [meta]
  );

  const tabCounts = useMemo<BillingCycleTabCounts | null>(
    () => (meta ? { ...EMPTY_TAB_COUNTS, ...meta.tab_counts } : null),
    [meta]
  );

  const insurances = useMemo<BillingOptionItem[]>(
    () =>
      (meta?.insurances ?? []).map(i => ({
        id: String(i.id),
        name: i.name,
      })),
    [meta]
  );

  const clinics = useMemo<BillingOptionItem[]>(
    () =>
      (meta?.clinics ?? []).map(c => ({
        id: String(c.id),
        name: c.name,
      })),
    [meta]
  );

  /** Reload the tab counts and option lists — used after a mutation changes
   *  which tab a claim belongs to. */
  const invalidateMetadata = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: billingKeys.billingMeta(),
    });
  }, [queryClient]);

  const handleRefresh = useCallback(async () => {
    if (!isMountedRef.current) {
      return;
    }

    setIsRefreshing(true);
    try {
      if (activeView === 'remittances') {
        // Remittances are ingested by upload/webhook — there is no billing-cycle
        // workflow to kick. "Refresh" here means re-read the current list (same
        // filters/sort/page) plus the payor options.
        await remittanceRef.current?.refresh();
        if (!isMountedRef.current) {
          return;
        }
        showSuccess('Remittances refreshed');
        return;
      }
      const startResponse = await refreshBillingCycles({});
      if (!isMountedRef.current) {
        return;
      }
      if (startResponse.error || !startResponse.data) {
        throw startResponse.error ?? new Error('No workflow id returned');
      }
      const workflowId = startResponse.data.workflow_id;
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        if (!isMountedRef.current) {
          return;
        }
        const statusResponse = await getRefreshBillingCyclesStatus({
          path: { workflow_id: workflowId },
        });
        if (!isMountedRef.current) {
          return;
        }
        if (statusResponse.error || !statusResponse.data) {
          throw statusResponse.error ?? new Error('No status returned');
        }
        const { status } = statusResponse.data;
        if (status === 'COMPLETED') {
          showSuccess('Billing cycles refreshed');
          // Invalidation rather than the old remount-by-key: every mounted tab
          // refetches in place, so the user's row selection survives a refresh
          // instead of being destroyed along with the component.
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: billingKeys.billingCycles(),
            }),
            invalidateMetadata(),
          ]);
          return;
        }
        if (TERMINAL_FAILURES.includes(status)) {
          throw new Error(`Refresh workflow ended with status ${status}`);
        }
      }
      if (!isMountedRef.current) {
        return;
      }
      showSuccess(
        'Refresh is still running in the background — reload this page shortly to see the results'
      );
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      console.error('Failed to refresh', error);
      showError(
        activeView === 'remittances'
          ? 'Failed to refresh remittances'
          : 'Failed to refresh billing cycles'
      );
    } finally {
      if (isMountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [activeView, invalidateMetadata, queryClient, showError, showSuccess]);

  const handleViewChange = useCallback((value: string) => {
    setActiveView(value as 'claims' | 'remittances');
  }, []);

  const handleTabChange = useCallback((value: string) => {
    const id = value as EoeClaimTabId;
    setActiveTab(id);
    setMountedTabIds(prev => new Set(prev).add(id));
  }, []);

  return (
    <div className='space-y-6'>
      <Tabs
        value={activeView}
        onValueChange={handleViewChange}
        className='flex w-full flex-col gap-6'
        variant='purple'
      >
        <div className='flex flex-wrap items-start justify-between gap-4'>
          <div>
            <h1 className='text-2xl font-bold'>
              {VIEW_HEADINGS[activeView].title}
            </h1>
            <p className='text-muted-foreground text-sm'>
              {VIEW_HEADINGS[activeView].description}
            </p>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <TabsList>
              <TabsTrigger value='claims'>Claims</TabsTrigger>
              <TabsTrigger value='remittances'>Remittances</TabsTrigger>
            </TabsList>
            <Button
              size='sm'
              variant='outline'
              onClick={() => void handleRefresh()}
              disabled={isRefreshing}
            >
              <RefreshCw
                className={
                  isRefreshing ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'
                }
              />
              {isRefreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        </div>

        <TabsContent value='claims' className='m-0'>
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className='flex w-full flex-col gap-6'
            variant='purple'
          >
            <TabsList className='grid h-auto min-h-8 w-full grid-cols-1 gap-0.5 rounded-md border border-border bg-muted/40 p-1 min-[520px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7'>
              {tabs.map(tab => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className='group min-h-8 whitespace-normal px-1 py-1.5 text-[11px] font-medium leading-tight [overflow-wrap:anywhere] data-[state=active]:shadow-none min-[520px]:px-2 sm:text-xs lg:text-[13px] xl:text-sm'
                >
                  <span className='inline-flex min-w-0 max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center [overflow-wrap:anywhere] sm:flex-nowrap'>
                    <span className='min-w-0 [overflow-wrap:anywhere] sm:whitespace-nowrap'>
                      {tab.label}
                    </span>
                    <TabCountPill
                      label={tab.label}
                      value={getCountText(tabCounts, tab.count_key)}
                    />
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>

            <Card>
              <CardContent className='py-6'>
                <EOEClaimsManagerTabProvider
                  activeTabId={activeTab}
                  insurances={insurances}
                  clinics={clinics}
                >
                  {tabs.map(tab => {
                    const TabComponent =
                      TAB_COMPONENTS[tab.id as EoeClaimTabId];
                    return (
                      <TabsContent key={tab.id} value={tab.id} className='m-0'>
                        {mountedTabIds.has(tab.id as EoeClaimTabId) ? (
                          <TabComponent
                            onCountsInvalidated={invalidateMetadata}
                          />
                        ) : null}
                      </TabsContent>
                    );
                  })}
                </EOEClaimsManagerTabProvider>
              </CardContent>
            </Card>
          </Tabs>
        </TabsContent>

        <TabsContent value='remittances' className='m-0'>
          <Card>
            <CardContent className='py-6'>
              <RemittanceManager ref={remittanceRef} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EOEClaimsManager;
